import {
    Image as ImageIcon,
    Wand2,
    SlidersHorizontal,
    Palette,
    Download,
    RefreshCw,
    FileCode2,
} from 'lucide-react'
import React, { useState, useRef, useCallback, useEffect } from 'react'
import { traceImageToSVG, withSolidBackground } from '../utils/traceImage'
import { svgToEps } from '../utils/svgToEps'

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

const PRESET_COLORS = [
    { name: 'White', value: '#FFFFFF' },
    { name: 'Red', value: '#E54D42' },
    { name: 'Pink', value: '#E03E6D' },
    { name: 'Purple', value: '#9834B7' },
    { name: 'Deep Blue', value: '#4A47A3' },
    { name: 'Royal Blue', value: '#3B79C9' },
    { name: 'Sky Blue', value: '#4FA0EC' },
    { name: 'Teal', value: '#3D8E83' },
    { name: 'Green', value: '#5CA85A' },
    { name: 'Yellow', value: '#FCE556' },
    { name: 'Orange', value: '#EE9331' },
    { name: 'Gray', value: '#9E9E9E' },
]

const COLOR_MODES = [
    { id: 'color', label: 'Full Color' },
    { id: 'reduced', label: 'Reduced Colors' },
    { id: 'grayscale', label: 'Grayscale' },
    { id: 'bw', label: 'Black & White' },
]

export default function ImageToVectorConverter() {
    const [originalUrl, setOriginalUrl] = useState(null)
    const [originalFile, setOriginalFile] = useState(null)
    const [fileName, setFileName] = useState('')
    const [status, setStatus] = useState('idle') // idle | loading | error | done
    const [errorMsg, setErrorMsg] = useState('')
    const [progress, setProgress] = useState(0)

    const [svgResult, setSvgResult] = useState(null)
    const [dims, setDims] = useState({ width: 0, height: 0 })

    // Vector Magic style controls
    const [colorMode, setColorMode] = useState('color')
    const [detail, setDetail] = useState(55)
    const [bgMode, setBgMode] = useState('transparent') // transparent | color
    const [bgColor, setBgColor] = useState('#FFFFFF')
    const [format, setFormat] = useState('svg') // svg | eps

    // Before/after comparison slider
    const [sliderPos, setSliderPos] = useState(50)
    const isDraggingSlider = useRef(false)
    const containerRef = useRef(null)
    const inputRef = useRef(null)
    const progressTimer = useRef(null)

    const reset = () => {
        setOriginalUrl(null)
        setOriginalFile(null)
        setFileName('')
        setStatus('idle')
        setErrorMsg('')
        setProgress(0)
        setSvgResult(null)
        setColorMode('color')
        setDetail(55)
        setBgMode('transparent')
        setBgColor('#FFFFFF')
        setFormat('svg')
        setSliderPos(50)
    }

    const runTrace = useCallback(
        async (file, opts) => {
            setStatus('loading')
            setErrorMsg('')
            setProgress(8)
            clearInterval(progressTimer.current)
            progressTimer.current = setInterval(() => {
                setProgress((p) => (p < 88 ? p + Math.random() * 12 : p))
            }, 140)

            try {
                const { svg, width, height } = await traceImageToSVG(file, {
                    colorMode: opts.colorMode,
                    detail: opts.detail,
                    transparentBackground: opts.bgMode === 'transparent',
                })
                clearInterval(progressTimer.current)
                setProgress(100)
                setSvgResult(svg)
                setDims({ width, height })
                setStatus('done')
            } catch (err) {
                console.error(err)
                clearInterval(progressTimer.current)
                setErrorMsg('Something went wrong while tracing this image. Please try a different file.')
                setStatus('error')
            }
        },
        []
    )

    const handleFile = useCallback(
        async (file) => {
            if (!file) return
            if (!ACCEPTED_TYPES.includes(file.type)) {
                setErrorMsg('Please upload a PNG, JPG, or WEBP image.')
                setStatus('error')
                return
            }
            setFileName(file.name)
            setOriginalUrl(URL.createObjectURL(file))
            setOriginalFile(file)
            await runTrace(file, { colorMode, detail, bgMode })
        },
        [runTrace, colorMode, detail, bgMode]
    )

    // Re-trace whenever settings change (debounced), Vector-Magic style live update
    useEffect(() => {
        if (!originalFile || status === 'idle' || status === 'error') return
        const t = setTimeout(() => {
            runTrace(originalFile, { colorMode, detail, bgMode })
        }, 350)
        return () => clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colorMode, detail, bgMode])

    useEffect(() => () => clearInterval(progressTimer.current), [])

    const onInputChange = (e) => handleFile(e.target.files?.[0])
    const onDrop = (e) => {
        e.preventDefault()
        handleFile(e.dataTransfer.files?.[0])
    }

    const handleSliderMove = (clientX) => {
        if (!containerRef.current || !isDraggingSlider.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const pct = ((clientX - rect.left) / rect.width) * 100
        setSliderPos(Math.max(0, Math.min(100, pct)))
    }

    const onMouseMove = useCallback((e) => handleSliderMove(e.clientX), [])
    const onTouchMove = useCallback((e) => handleSliderMove(e.touches[0].clientX), [])
    const onMouseUp = useCallback(() => {
        isDraggingSlider.current = false
    }, [])

    useEffect(() => {
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)
        window.addEventListener('touchmove', onTouchMove)
        window.addEventListener('touchend', onMouseUp)
        return () => {
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
            window.removeEventListener('touchmove', onTouchMove)
            window.removeEventListener('touchend', onMouseUp)
        }
    }, [onMouseMove, onMouseUp, onTouchMove])

    const displaySvg =
        bgMode === 'color' && svgResult
            ? withSolidBackground(svgResult, dims.width, dims.height, bgColor)
            : svgResult

    const onDownload = () => {
        if (!svgResult) return
        const base = fileName.replace(/\.[^/.]+$/, '') || 'vector'

        if (format === 'svg') {
            const finalSvg =
                bgMode === 'color'
                    ? withSolidBackground(svgResult, dims.width, dims.height, bgColor)
                    : svgResult
            const blob = new Blob([finalSvg], { type: 'image/svg+xml' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${base}.svg`
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)
        } else {
            const eps = svgToEps(
                svgResult,
                dims.width,
                dims.height,
                bgMode === 'color' ? { color: bgColor } : null
            )
            const blob = new Blob([eps], { type: 'application/postscript' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${base}.eps`
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)
        }
    }

    const svgDataUrl = displaySvg
        ? `data:image/svg+xml;utf8,${encodeURIComponent(displaySvg)}`
        : null

    return (
        <div className="min-h-screen bg-[#0F0F0F] flex text-white relative overflow-hidden flex-col gap-5">
            <div className="absolute top-10 left-4 sm:top-20 sm:left-20 w-40 h-40 sm:w-72 sm:h-72 bg-[#0B3D2E]/40 rounded-full blur-[80px] sm:blur-[120px]" />
            <div className="absolute bottom-10 right-4 sm:bottom-20 sm:right-20 w-40 h-40 sm:w-72 sm:h-72 bg-[#145A43]/25 rounded-full blur-[90px] sm:blur-[140px]" />

            {/* Header */}
            <div className="bg-gradient-to-br from-[#0B3D2E] via-[#145A43] to-[#0B3D2E] border-b border-[#2F8F6F]/20 px-6 py-12 md:py-16 shadow-lg">
                <div className="max-w-2xl mx-auto flex flex-col justify-center items-center text-center space-y-3">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-[#D7C7A1]">
                        Image to Vector Converter
                    </h1>
                    <p className="text-[#D7C7A1] text-xs sm:text-sm max-w-md font-medium leading-relaxed">
                        Trace raster images into clean vector paths, right in your browser.
                        <br className="hidden sm:block" />
                        <span className="block sm:inline"> No upload. No server. Export as SVG or EPS.</span>
                    </p>
                </div>
            </div>

            <div className="px-4 sm:px-8 md:px-10 relative mb-10">
                <div className="relative z-10 w-full max-w-5xl mx-auto rounded-2xl sm:rounded-[32px] bg-white/5 backdrop-blur-2xl border border-[#2F8F6F]/15 p-4 sm:p-8 md:p-10">
                    {status === 'idle' && (
                        <label
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={onDrop}
                            className="group flex flex-col items-center justify-center gap-2 sm:gap-3 rounded-2xl sm:rounded-3xl border-2 border-dashed border-[#2F8F6F]/30 bg-[#0B3D2E]/10 h-[220px] sm:h-[300px] px-4 text-center cursor-pointer transition-all duration-300 hover:border-[#2F8F6F]/60 hover:bg-[#0B3D2E]/20 hover:shadow-[0_0_60px_rgba(47,143,111,0.2)]"
                        >
                            <div className="group-hover:scale-110 transition text-[#2F8F6F]">
                                <ImageIcon size={40} className="sm:hidden" />
                                <ImageIcon size={50} className="hidden sm:block" />
                            </div>
                            <h3 className="text-lg sm:text-xl font-bold text-white">Drop your image here</h3>
                            <p className="text-[#D7C7A1]/50 text-sm sm:text-base">Click to browse • PNG • JPG • WEBP</p>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault()
                                    inputRef.current?.click()
                                }}
                                className="mt-2 sm:mt-3 px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-full bg-gradient-to-r from-[#2F8F6F] to-[#145A43] shadow-lg shadow-[#2F8F6F]/30 hover:scale-105 hover:shadow-[#2F8F6F]/50 transition-all duration-300 text-white font-semibold"
                            >
                                Choose Image
                            </button>
                            <input
                                ref={inputRef}
                                type="file"
                                accept={ACCEPTED_TYPES.join(',')}
                                className="hidden"
                                onChange={onInputChange}
                            />
                        </label>
                    )}

                    {status === 'loading' && (
                        <div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                <div>
                                    <h3 className="mb-2 sm:mb-3 text-[#2F8F6F] font-semibold text-sm sm:text-base">Original Image</h3>
                                    <img
                                        src={originalUrl}
                                        alt="Original"
                                        className="w-full h-[220px] sm:h-[300px] md:h-[350px] object-contain rounded-2xl sm:rounded-3xl bg-black/30 border border-[#145A43]/40"
                                    />
                                </div>
                                <div>
                                    <h3 className="mb-2 sm:mb-3 text-[#2F8F6F] font-semibold text-sm sm:text-base">Tracing to Vector</h3>
                                    <div className="relative w-full h-[220px] sm:h-[300px] md:h-[350px] rounded-2xl sm:rounded-3xl border border-[#145A43]/40 overflow-hidden flex items-center justify-center bg-checker">
                                        <img src={originalUrl} alt="" className="absolute inset-0 w-full h-full object-contain opacity-20 blur-sm" />
                                        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-[#2F8F6F]/10 to-transparent" />
                                        <div className="absolute inset-4 sm:inset-6 rounded-xl sm:rounded-2xl bg-[#0B3D2E]/20 animate-pulse" />
                                    </div>
                                </div>
                            </div>
                            <div className="w-full max-w-md mx-auto mt-6 sm:mt-8">
                                <div className="h-2.5 sm:h-3 bg-white/10 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-[#2F8F6F] to-[#D7C7A1] transition-all duration-300"
                                        style={{ width: `${Math.min(100, progress)}%` }}
                                    />
                                </div>
                                <p className="text-center mt-3 sm:mt-4 text-sm sm:text-base text-[#D7C7A1]/70">
                                    Tracing paths...{progress > 0 && ` ${Math.round(Math.min(100, progress))}%`}
                                </p>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="text-center py-6 sm:py-10 px-2">
                            <p className="text-red-400 mb-4 sm:mb-5 text-sm sm:text-base">{errorMsg}</p>
                            <button
                                onClick={reset}
                                className="px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-full bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 transition"
                            >
                                Try Again
                            </button>
                        </div>
                    )}

                    {status === 'done' && (
                        <div className="flex flex-col gap-6">
                            {/* Before / after comparison slider */}
                            <div className="w-full flex flex-col items-center">
                                <h3 className="flex flex-row gap-2 items-center justify-center mb-3 text-[#2F8F6F] font-semibold text-sm sm:text-base text-center w-full">
                                    <SlidersHorizontal size={18} /> Drag to compare original vs. vector
                                </h3>
                                <div className="w-full max-w-full overflow-hidden flex items-center justify-center p-1">
                                    <div
                                        ref={containerRef}
                                        className="relative w-full h-[300px] sm:h-[400px] rounded-2xl sm:rounded-3xl border border-[#145A43]/40 overflow-hidden select-none touch-none bg-checker"
                                    >
                                        {/* Original (bottom layer) */}
                                        <img
                                            src={originalUrl}
                                            alt="Original"
                                            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                                        />
                                        {/* Vector result (top layer, clipped by slider) */}
                                        <div
                                            className="absolute inset-0 w-full h-full flex items-center justify-center"
                                            style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
                                        >
                                            {svgDataUrl && (
                                                <img
                                                    src={svgDataUrl}
                                                    alt="Vector result"
                                                    className="max-h-full max-w-full object-contain pointer-events-none"
                                                />
                                            )}
                                        </div>
                                        {/* Divider handle */}
                                        <div
                                            className="absolute top-0 bottom-0 w-1 bg-[#D7C7A1] cursor-ew-resize"
                                            style={{ left: `${sliderPos}%` }}
                                            onMouseDown={() => {
                                                isDraggingSlider.current = true
                                            }}
                                            onTouchStart={() => {
                                                isDraggingSlider.current = true
                                            }}
                                        >
                                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#D7C7A1] shadow-lg flex items-center justify-center">
                                                <SlidersHorizontal size={14} className="text-[#0F0F0F]" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Color mode controls */}
                            <div className="p-4 rounded-xl bg-white/5 border border-[#2F8F6F]/10 space-y-3">
                                <span className="text-sm font-semibold text-[#D7C7A1] flex items-center gap-2">
                                    <Wand2 size={16} className="text-[#2F8F6F]" /> Color Mode
                                </span>
                                <div className="flex flex-wrap gap-2.5">
                                    {COLOR_MODES.map((mode) => (
                                        <button
                                            key={mode.id}
                                            onClick={() => setColorMode(mode.id)}
                                            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${colorMode === mode.id
                                                ? 'bg-[#2F8F6F] text-white border border-[#2F8F6F]'
                                                : 'bg-[#0F0F0F]/60 text-gray-300 border border-white/10 hover:border-white/30'
                                                }`}
                                        >
                                            {mode.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Detail slider */}
                            <div className="p-4 rounded-xl bg-white/5 border border-[#2F8F6F]/10 space-y-3">
                                <div className="flex flex-row items-center justify-between">
                                    <span className="text-xs md:text-sm font-semibold text-[#D7C7A1] flex items-center gap-2">
                                        <SlidersHorizontal size={16} className="text-[#2F8F6F]" /> Detail Level
                                    </span>
                                    <span className="text-xs font-mono text-gray-300">{detail}</span>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={detail}
                                    onChange={(e) => setDetail(parseInt(e.target.value, 10))}
                                    className="w-full accent-[#2F8F6F] bg-white/10 h-1.5 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-[10px] text-gray-500">
                                    <span>Simplified paths</span>
                                    <span>Maximum detail</span>
                                </div>
                            </div>

                            {/* Background controls */}
                            <div className="p-5 rounded-2xl bg-white/5 border border-[#2F8F6F]/10 space-y-4">
                                <span className="text-sm font-semibold text-[#D7C7A1] flex items-center gap-2">
                                    <Palette size={16} className="text-[#2F8F6F]" /> Background
                                </span>
                                <div className="flex flex-row flex-wrap gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setBgMode('transparent')}
                                        className={`w-[60px] h-[60px] md:w-16 md:h-16 rounded-2xl flex items-center justify-center bg-checker border-2 transition-all relative overflow-hidden group ${bgMode === 'transparent'
                                            ? 'border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.5)] scale-95'
                                            : 'border-white/20 hover:border-white/40'
                                            }`}
                                        title="Transparent"
                                    >
                                        <span className="text-xl font-light text-black/60 select-none">🛇</span>
                                    </button>

                                    <label
                                        className={`w-[60px] h-[60px] md:w-16 md:h-16 rounded-2xl cursor-pointer transition-all relative overflow-hidden border-2 ${bgMode === 'color' && !PRESET_COLORS.some((c) => c.value.toLowerCase() === bgColor.toLowerCase())
                                            ? 'border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.5)] scale-95'
                                            : 'border-white/20 hover:border-white/40'
                                            }`}
                                        style={{
                                            background:
                                                'conic-gradient(from 180deg at 50% 50%, #FF0000 0deg, #FF00FF 60deg, #0000FF 120deg, #00FFFF 180deg, #00FF00 240deg, #FFFF00 300deg, #FF0000 360deg)',
                                        }}
                                    >
                                        <input
                                            type="color"
                                            value={bgColor}
                                            onChange={(e) => {
                                                setBgColor(e.target.value)
                                                setBgMode('color')
                                            }}
                                            className="sr-only"
                                        />
                                    </label>

                                    {PRESET_COLORS.map((color) => {
                                        const isSelected = bgMode === 'color' && bgColor.toLowerCase() === color.value.toLowerCase()
                                        return (
                                            <button
                                                key={color.name}
                                                type="button"
                                                onClick={() => {
                                                    setBgColor(color.value)
                                                    setBgMode('color')
                                                }}
                                                className={`w-[60px] h-[60px] md:w-16 md:h-16 rounded-2xl border-2 transition-all ${isSelected
                                                    ? 'border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.5)] scale-95'
                                                    : 'border-white/10 hover:border-white/30'
                                                    }`}
                                                style={{ backgroundColor: color.value }}
                                                title={color.name}
                                            />
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Format controls */}
                            <div className="p-4 rounded-xl bg-white/5 border border-[#2F8F6F]/10 space-y-3">
                                <span className="text-sm font-semibold text-[#D7C7A1] flex items-center gap-2">
                                    <FileCode2 size={16} className="text-[#2F8F6F]" /> Export Format
                                </span>
                                <div className="flex flex-wrap gap-2.5">
                                    {['svg', 'eps'].map((f) => (
                                        <button
                                            key={f}
                                            onClick={() => setFormat(f)}
                                            className={`px-5 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide transition-all ${format === f
                                                ? 'bg-[#2F8F6F] text-white border border-[#2F8F6F]'
                                                : 'bg-[#0F0F0F]/60 text-gray-300 border border-white/10 hover:border-white/30'
                                                }`}
                                        >
                                            .{f}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-3 sm:gap-4 mt-2">
                                <button
                                    onClick={onDownload}
                                    className="w-full sm:w-auto px-8 py-3 text-sm sm:text-base rounded-full bg-[#2F8F6F] font-bold text-[#0F0F0F] hover:scale-105 transition flex items-center justify-center gap-2"
                                >
                                    <Download size={18} /> Download .{format}
                                </button>
                                <button
                                    onClick={reset}
                                    className="w-full sm:w-auto px-8 py-3 text-sm sm:text-base rounded-full bg-[#0B3D2E]/30 backdrop-blur border border-[#2F8F6F]/20 text-[#D7C7A1] hover:bg-[#0B3D2E]/50 hover:border-[#2F8F6F]/40 transition flex items-center justify-center gap-2"
                                >
                                    <RefreshCw size={16} /> Try Another Image
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
